import React, { useState } from 'react';
import { useForm } from '@formspree/react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle2, 
  Globe, 
  Award, 
  Factory, 
  Users, 
  ShieldCheck,
  Phone,
  Mail,
  MapPin,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import useTranslation from '../hooks/useTranslation';

const Home = () => {
  const { t, language } = useTranslation();
  const [state, handleSubmit] = useForm('mjgjvven');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    phone: '',
    service: '',
    message: ''
  });

  React.useEffect(() => {
    const canonicalUrl = `https://global.maharya.com/${language}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
    document.title = t.meta.title;
    const descTag = document.querySelector('meta[name="description"]');
    if (descTag) descTag.setAttribute('content', t.meta.description);
    const keywordsTag = document.querySelector('meta[name="keywords"]');
    if (keywordsTag) keywordsTag.setAttribute('content', t.meta.keywords);
    document.documentElement.setAttribute('lang', language);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', t.meta.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', t.meta.description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', t.meta.title);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', t.meta.description);
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) twitterUrl.setAttribute('content', canonicalUrl);
  }, [language, t]);

  React.useEffect(() => {
    if (state.succeeded) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'Bulk Quote Form',
          event_label: language === 'en' ? 'English' : language === 'es' ? 'Spanish' : 'French',
          value: 50,
          currency: 'USD'
        });
      }
      setTimeout(() => {
        window.location.href = `/${language}/thank-you`;
      }, 1500);
    }
  }, [state.succeeded, language]);

  React.useEffect(() => {
    if (state.errors && state.errors.length > 0) {
      toast.error(t.contact.form.errorMessage);
    }
  }, [state.errors, t]);

  React.useEffect(() => {
    const handleScroll = () => {
      // Show sticky CTA only after user scrolls past hero (~600px) AND not when contact section is visible
      const scrolled = window.scrollY > 600;
      const contactEl = document.getElementById('contact');
      const contactInView = contactEl ? contactEl.getBoundingClientRect().top < window.innerHeight - 200 : false;
      setShowStickyCta(scrolled && !contactInView);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-stone-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <a href="https://www.maharya.com" target="_blank" rel="noopener noreferrer">
                <img loading="lazy" 
                  src="https://cdn.prod.website-files.com/66892da993ba92c0c571f33e/6a181f5a46bd08018fe9b3c8_Maharya%20logo%20with%20R%20symbol.png"
                  alt="Maharya"
                  className="h-8 md:h-10 w-auto"
                />
              </a>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#manufacturing" className="text-stone-700 hover:text-stone-900 transition-colors">{t.nav.manufacturing}</a>
              <a href="#capabilities" className="text-stone-700 hover:text-stone-900 transition-colors">{t.nav.capabilities}</a>
              <a href="#why-us" className="text-stone-700 hover:text-stone-900 transition-colors">{t.nav.whyUs}</a>
              <a href="#contact" className="text-stone-700 hover:text-stone-900 transition-colors">{t.nav.contact}</a>
              <LanguageSwitcher />
            </nav>
            <div className="flex items-center gap-4">
              {/* Phone — display only, not clickable */}
              <span className="hidden md:block text-stone-600 text-sm font-medium px-3 py-2 bg-stone-100 rounded-md select-all">
                +1-917-730-4220
              </span>
              {/* Book a Call — primary action */}
              <a href="https://tidycal.com/leather-accessories/30-minute-meeting" target="_blank" rel="noopener noreferrer" className="hidden md:block">
                <Button className="bg-[#7B3F00] hover:bg-[#5A2F00] text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Réserver' : language === 'es' ? 'Reservar' : 'Book a Call'}
                </Button>
              </a>
              <button 
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-stone-700" />
                ) : (
                  <Menu className="w-6 h-6 text-stone-700" />
                )}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200">
            <nav className="px-4 py-4 space-y-3">
              <a href="#manufacturing" className="block py-2 text-stone-700 hover:text-stone-900 font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.manufacturing}</a>
              <a href="#capabilities" className="block py-2 text-stone-700 hover:text-stone-900 font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.capabilities}</a>
              <a href="#why-us" className="block py-2 text-stone-700 hover:text-stone-900 font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.whyUs}</a>
              <a href="#contact" className="block py-2 text-stone-700 hover:text-stone-900 font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.contact}</a>
              <div className="pt-2 border-t border-stone-200">
                <LanguageSwitcher />
              </div>
              {/* Phone — display only, not clickable */}
              <p className="py-2 text-stone-600 font-medium text-sm select-all">+1-917-730-4220</p>
              {/* Book a Call — primary action */}
              <a href="https://tidycal.com/leather-accessories/30-minute-meeting" target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full bg-[#7B3F00] hover:bg-[#5A2F00] text-white mt-2">
                  <Phone className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Réserver' : language === 'es' ? 'Reservar' : 'Book a Call'}
                </Button>
              </a>
            </nav>
          </div>
        )}
      </header>

      <section className="relative h-screen flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0 z-0">
          <picture>
            <source
              media="(max-width: 768px)"
              srcSet="https://images.pexels.com/photos/4452610/pexels-photo-4452610.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop&q=55"
            />
            <source
              media="(max-width: 1280px)"
              srcSet="https://images.pexels.com/photos/4452610/pexels-photo-4452610.jpeg?auto=compress&cs=tinysrgb&w=1280&h=720&fit=crop&q=60"
            />
            <img 
              src="https://images.pexels.com/photos/4452610/pexels-photo-4452610.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&q=60"
              alt="Certified leather accessories manufacturing facility — Maharya, Kolkata"
              className="w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width="1920"
              height="1080"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 to-stone-800/70"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-8 sm:px-12 lg:px-16 xl:px-20 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 leading-tight overflow-hidden max-w-4xl mx-auto">
            {t.hero.title}
            <span className="block text-amber-500 mt-3 text-base sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold leading-snug px-2">{t.hero.subtitle}</span>
          </h1>
          {/* Product categories — translated per language */}
          <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mb-5 px-4">
            {(language === 'es'
              ? ["Carteras", "Bolsos", "Cinturones", "Tarjeteros", "Regalos Corporativos"]
              : language === 'fr'
              ? ["Portefeuilles", "Sacs", "Ceintures", "Porte-cartes", "Cadeaux d'entreprise"]
              : ["Wallets", "Bags", "Belts", "Cardholders", "Corporate Gifts"]
            ).map((item, i, arr) => (
              <span key={item} className="text-amber-500 text-xs sm:text-sm font-medium">
                {item}{i < arr.length - 1 ? <span className="text-stone-500 mx-1">·</span> : ""}
              </span>
            ))}
          </div>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-stone-200 mb-5 max-w-3xl mx-auto px-4">
            {t.hero.description}
          </p>
          {/* Location — translated per language */}
          <div className="flex flex-nowrap justify-center items-center gap-x-2 mb-7 text-xs text-stone-400 px-4">
            <span className="whitespace-nowrap">🏭 {language === 'es' ? 'Fábrica: Kolkata, India' : language === 'fr' ? 'Usine: Kolkata, Inde' : 'Factory: Kolkata, India'}</span>
            <span className="text-white mx-2 opacity-50">·</span>
            <span className="whitespace-nowrap">📍 {language === 'es' ? 'Oficina EE.UU.: Albany, NY' : language === 'fr' ? 'Bureau USA: Albany, NY' : 'US Office: Albany, NY'}</span>
          </div>
          <div className="flex justify-center">
            <a href="#quote-form">
              <Button size="lg" className="bg-[#7B3F00] hover:bg-[#5A2F00] text-white text-lg sm:text-xl px-10 sm:px-14 py-7 shadow-2xl shadow-amber-900/40 font-bold">
                {t.hero.cta1} →
              </Button>
            </a>
          </div>
          <p className="text-stone-300 text-sm mt-4 max-w-md mx-auto">
            {t.hero.ctaSubtext}
          </p>
          <div className="mt-5">
            <a 
              href="https://www.maharya.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-amber-500 hover:text-amber-400 text-xs sm:text-sm font-bold transition-colors underline underline-offset-2"
            >
              {language === 'fr' ? 'Notre usine et certifications →' : language === 'es' ? 'Nuestra fábrica y certificaciones →' : 'Our Factory & Certifications →'}
            </a>
          </div>
          <div className="mt-6">
            <p className="text-amber-500 text-xs sm:text-sm font-bold tracking-widest mb-4 uppercase">{t.contact.form.trustedBy}</p>
            <div className="flex flex-wrap justify-center gap-3 items-center">
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 text-white text-xs sm:text-sm font-semibold">WRAP Certified</div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 text-white text-xs sm:text-sm font-semibold">C-TPAT</div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 text-white text-xs sm:text-sm font-semibold">SEDEX SMETA</div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 text-white text-xs sm:text-sm font-semibold">ISO 9001/45001</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center"><div className="text-4xl md:text-5xl font-bold text-[rgb(123,63,0)] mb-2">27+</div><div className="text-stone-600 font-medium">{t.stats.experience}</div></div>
            <div className="text-center"><div className="text-4xl md:text-5xl font-bold text-[rgb(123,63,0)] mb-2">30K+</div><div className="text-stone-600 font-medium">{t.stats.units}</div></div>
            <div className="text-center"><div className="text-4xl md:text-5xl font-bold text-[rgb(123,63,0)] mb-2">100+</div><div className="text-stone-600 font-medium">{t.stats.clients}</div></div>
            <div className="text-center"><div className="text-4xl md:text-5xl font-bold text-[rgb(123,63,0)] mb-2">25+</div><div className="text-stone-600 font-medium">{t.stats.countries}</div></div>
          </div>
        </div>
      </section>

      <section id="manufacturing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">{t.manufacturing.title}</h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">{t.manufacturing.subtitle}</p>
            <p className="text-lg text-[rgb(123,63,0)] font-semibold mt-4">{t.manufacturing.tagline}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-stone-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <img loading="lazy" decoding="async"
                  src="https://images.unsplash.com/photo-1534126511673-b6899657816a?w=800&h=600&q=75&auto=format&fit=crop"
                  alt={t.manufacturing.features[0].imageAlt}
                  className="w-full h-48 object-cover rounded-lg mb-6"
                  width="800" height="600"
                />
                <h3 className="text-2xl font-bold text-stone-800 mb-4">{t.manufacturing.features[0].title}</h3>
                <p className="text-stone-600 leading-relaxed">{t.manufacturing.features[0].description}</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <img loading="lazy" decoding="async"
                  src="https://wsrv.nl/?url=customer-assets.emergentagent.com/job_maharya-b2b/artifacts/hvbhx1tx_Factory%20Images_Women%20work%20force_Far%20view.png&w=800&h=600&fit=cover&output=webp&q=78"
                  alt={t.manufacturing.features[1].imageAlt}
                  className="w-full h-48 object-cover rounded-lg mb-6"
                  width="800" height="600"
                />
                <h3 className="text-2xl font-bold text-stone-800 mb-4">{t.manufacturing.features[1].title}</h3>
                <p className="text-stone-600 leading-relaxed">{t.manufacturing.features[1].description}</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <img loading="lazy" decoding="async"
                  src="https://images.unsplash.com/photo-1599694522028-65abc96dfd2f?w=800&h=600&q=75&auto=format&fit=crop"
                  alt={t.manufacturing.features[2].imageAlt}
                  className="w-full h-48 object-cover rounded-lg mb-6"
                  width="800" height="600"
                />
                <h3 className="text-2xl font-bold text-stone-800 mb-4">{t.manufacturing.features[2].title}</h3>
                <p className="text-stone-600 leading-relaxed">{t.manufacturing.features[2].description}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section id="capabilities" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">{t.capabilities.title}</h2>
            <p className="text-xl text-stone-600">{t.capabilities.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="relative group overflow-hidden rounded-lg">
              <img loading="lazy" decoding="async"
                src="https://wsrv.nl/?url=customer-assets.emergentagent.com/job_maharya-b2b/artifacts/p3mvxw96_MB_5694_LIF3_WR_BLACK.jpg&w=900&h=1000&fit=cover&output=webp&q=78"
                alt={t.capabilities.categories[0].imageAlt}
                className="w-full h-80 object-cover transition-transform group-hover:scale-105"
                width="900" height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent flex items-end p-6">
                <h3 className="text-2xl font-bold text-white">{t.capabilities.categories[0].name}</h3>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-lg">
              <img loading="lazy" decoding="async"
                src="https://wsrv.nl/?url=customer-assets.emergentagent.com/job_maharya-b2b/artifacts/ng12wzki_LA_1384MH_LIF_BLACk%20wallet.jpg&w=900&h=1000&fit=cover&output=webp&q=78"
                alt={t.capabilities.categories[1].imageAlt}
                className="w-full h-80 object-cover transition-transform group-hover:scale-105"
                width="900" height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent flex items-end p-6">
                <h3 className="text-2xl font-bold text-white">{t.capabilities.categories[1].name}</h3>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-lg">
              <img loading="lazy" decoding="async"
                src="https://wsrv.nl/?url=customer-assets.emergentagent.com/job_maharya-b2b/artifacts/40bempoh_LA_WPB1010_LIF3_BROWN_WR.jpg&w=900&h=1000&fit=cover&output=webp&q=78"
                alt={t.capabilities.categories[2].imageAlt}
                className="w-full h-80 object-cover transition-transform group-hover:scale-105"
                width="900" height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent flex items-end p-6">
                <h3 className="text-2xl font-bold text-white">{t.capabilities.categories[2].name}</h3>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {t.capabilities.services.map((capability, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-stone-200">
                <CheckCircle2 className="w-6 h-6 text-[rgb(123,63,0)] flex-shrink-0" />
                <span className="text-stone-800 font-medium">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="why-us" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">{t.whyUs.title}</h2>
            <p className="text-xl text-stone-600">{t.whyUs.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.whyUs.reasons.map((reason, idx) => {
              const icons = [Award, Globe, Factory, ShieldCheck, Users, CheckCircle2];
              const Icon = icons[idx] || CheckCircle2;
              return (
                <Card key={idx} className="border-stone-200">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-[rgb(123,63,0)]" />
                    </div>
                    <h3 className="text-2xl font-bold text-stone-800 mb-4">{reason.title}</h3>
                    <p className="text-stone-600 leading-relaxed mb-4">{reason.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-6">{t.contact.title}</h2>
              <p className="text-xl text-stone-600 mb-8">{t.contact.description}</p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[rgb(123,63,0)]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-800 mb-1">{t.contact.officeLabel}</h3>
                    <p className="text-stone-600">418 Broadway #6782, Albany, NY 12207, USA</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[rgb(123,63,0)]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-800 mb-1">{t.contact.phoneLabel}</h3>
                    <p className="text-stone-600">+1-917-730-4220</p>
                    <p className="text-stone-600">+91-33-2414-1992 (India)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[rgb(123,63,0)]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-800 mb-1">{t.contact.emailLabel}</h3>
                    <p className="text-stone-600">contact@maharya.com</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Card id="quote-form" className="border-stone-200 shadow-lg scroll-mt-24">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-stone-800 mb-4">{t.contact.form.title}</h3>
                  {/* Book a Call — full tappable button, works on all screen sizes */}
                  <a
                    href="https://tidycal.com/leather-accessories/30-minute-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full border-2 border-[#7B3F00] text-[#7B3F00] font-semibold rounded-md py-3 px-4 mb-3 text-sm hover:bg-amber-50 active:bg-amber-100 transition-colors min-h-[48px]"
                  >
                    {t.contact.form.bookCall}
                  </a>
                  <p className="text-center text-xs text-stone-400 mb-4">{t.contact.form.orFillForm}</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.fullName}</label>
                      <Input type="text" name="name" value={formData.name} onChange={handleChange} required className="border-stone-300 text-base min-h-[48px]" placeholder={t.contact.form.fullNamePlaceholder} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.email}</label>
                      <Input type="email" name="email" value={formData.email} onChange={handleChange} required className="border-stone-300 text-base min-h-[48px]" placeholder={t.contact.form.emailPlaceholder} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.company} *</label>
                      <Input type="text" name="company" value={formData.company} onChange={handleChange} required className="border-stone-300 text-base min-h-[48px]" placeholder={t.contact.form.companyPlaceholder} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.website} *</label>
                      <Input type="text" name="website" value={formData.website} onChange={handleChange} required className="border-stone-300 text-base min-h-[48px]" placeholder={t.contact.form.websitePlaceholder} />
                    </div>
                    <div>
                      <label htmlFor="service-select" className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.serviceInterest} *</label>
                      <select id="service-select" name="service" value={formData.service} onChange={handleChange} required className="w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-700 text-base min-h-[48px]">
                        <option value="">{t.contact.form.serviceSelect}</option>
                        {t.contact.form.services.map((svc, idx) => (
                          <option key={idx} value={svc}>{svc}</option>
                        ))}
                      </select>
                    </div>
                    {/* Phone above Project Details — callback option visible before the hardest field */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.phone} <span className="text-stone-400 font-normal">(optional)</span></label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="border-stone-300 text-base min-h-[48px]" placeholder={t.contact.form.phonePlaceholder} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{t.contact.form.message}</label>
                      <Textarea name="message" value={formData.message} onChange={handleChange} required rows={3} className="border-stone-300 text-base" placeholder={t.contact.form.messagePlaceholder} />
                    </div>
                    {/* Reassurance bar — visible at decision moment on all screen sizes */}
                    <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5">
                      <div className="w-5 h-5 bg-[#7B3F00] rounded-full flex items-center justify-center flex-shrink-0">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <p className="text-xs text-amber-900 leading-snug">{t.contact.form.reassurance}</p>
                    </div>
                    <Button type="submit" disabled={state.submitting} className="w-full bg-[#7B3F00] hover:bg-[#5A2F00] text-white py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]">
                      {state.submitting ? t.contact.form.submitting : t.contact.form.submit}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-stone-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <a href="https://www.maharya.com" target="_blank" rel="noopener noreferrer">
                <img loading="lazy" 
                  src="https://cdn.prod.website-files.com/66892da993ba92c0c571f33e/6a181f5a46bd08018fe9b3c8_Maharya%20logo%20with%20R%20symbol.png"
                  alt="Maharya"
                  className="h-8 w-auto mb-4 brightness-0 invert"
                />
              </a>
              <p className="text-stone-400">{t.footer.description}</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t.footer.services.title}</h4>
              <ul className="space-y-2 text-stone-400">
                <li><a href="https://www.maharya.com/leather-manufacturing-services#Leather-Accessories-Manufacturing" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Fabrication sous marque privée' : language === 'es' ? 'Fabricación de marca privada' : 'Private Label Manufacturing'}</a></li>
                <li><a href="https://www.maharya.com/leather-manufacturing-services#Leather-Accessories-Manufacturing" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Production OEM / ODM' : language === 'es' ? 'Producción OEM / ODM' : 'OEM / ODM Production'}</a></li>
                <li><a href="https://www.maharya.com/leather-manufacturing-services#wholesale-orders" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Distribution en gros' : language === 'es' ? 'Distribución mayorista' : 'Wholesale Distribution'}</a></li>
                <li><a href="https://www.maharya.com/leather-manufacturing-services#Leather-Accessories-Manufacturing" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Cadeaux d\'entreprise (en gros)' : language === 'es' ? 'Regalos corporativos (a granel)' : 'Corporate Gifting (Bulk)'}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t.footer.company.title}</h4>
              <ul className="space-y-2 text-stone-400">
                <li><a href="https://www.maharya.com/about-us" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'À propos' : language === 'es' ? 'Acerca de nosotros' : 'About Us'}</a></li>
                <li><a href="https://www.maharya.com/certifications" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Certifications' : language === 'es' ? 'Certificaciones' : 'Certifications'}</a></li>
                <li><a href="https://www.maharya.com/sustainability" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Durabilité' : language === 'es' ? 'Sostenibilidad' : 'Sustainability'}</a></li>
                <li><a href="https://www.maharya.com/leather-manufacturing-services" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-white transition-colors min-h-[44px] flex items-center">{language === 'fr' ? 'Normes de qualité' : language === 'es' ? 'Estándares de calidad' : 'Quality Standards'}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t.footer.contact.title}</h4>
              <ul className="space-y-2 text-stone-400">
                <li>+1-917-730-4220</li>
                <li>contact@maharya.com</li>
                <li>Albany, NY, USA</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-8 text-center text-stone-400">
            <p>{t.footer.copyright}</p>
            <p className="mt-2">
              <a href="https://www.maharya.com" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-amber-700 transition-colors">www.maharya.com</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA - hidden on hero & contact section */}
      <a
        href="#quote-form"
        className={`md:hidden fixed bottom-4 left-4 right-4 z-40 bg-[#7B3F00] hover:bg-[#5A2F00] text-white font-bold text-base py-4 px-6 rounded-full shadow-2xl flex items-center justify-center gap-2 transition-all duration-300 active:scale-95 ${
          showStickyCta ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-8 pointer-events-none'
        }`}
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        data-testid="sticky-mobile-cta"
      >
        {t.contact.form.stickyCta} →
      </a>
    </div>
  );
};

export default Home;
